
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { QuotationsDataTable } from "./quotation/QuotationsDataTable";
import { RefreshCw } from "lucide-react";

interface QuotationTableProps {
  onEdit: (quotation: any) => void;
  onDelete?: (quotation: any) => void;
  onView?: (quotation: any) => void;
  onShare?: (quotation: any) => void;
}

export function QuotationTable({ onEdit, onDelete, onView, onShare }: QuotationTableProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: quotations, isLoading, refetch, error } = useQuery({
    queryKey: ["quotations"],
    queryFn: async () => {
      console.log("Executando consulta de orçamentos...");
      
      // Force network request without using cache
      const { data, error } = await supabase
        .from("quotations")
        .select(`
          *,
          customer:customers(id, name, whatsapp, phone)
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar orçamentos:", error);
        throw error;
      }
      
      console.log(`Orçamentos encontrados: ${data?.length || 0}`);
      return data;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 10000, // 10 seconds before data becomes stale
    gcTime: 60 * 1000, // 1 minute garbage collection time
  });

  // Log any errors that occur during query execution
  useEffect(() => {
    if (error) {
      console.error("Erro na consulta de orçamentos:", error);
      toast({
        title: "Erro ao carregar orçamentos",
        description: "Ocorreu um erro ao carregar os dados. Tente novamente.",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  // Listen for real-time updates with improved logging and reliability
  useEffect(() => {
    console.log("Setting up quotation real-time listener");
    
    const channel = supabase
      .channel("quotation-real-time-channel")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "quotations",
        },
        (payload) => {
          console.log("Quotation real-time event received:", payload.eventType, payload);
          
          // Invalidate queries to update UI
          queryClient.invalidateQueries({ queryKey: ["quotations"] });
          
          // Clear service worker cache for quotations if service worker is available
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'CLEAR_QUOTATIONS_CACHE'
            });
          }
          
          // Show toast notification based on event type
          if (payload.eventType === 'INSERT') {
            toast({
              title: "Novo orçamento",
              description: "Um novo orçamento foi adicionado.",
            });
          } else if (payload.eventType === 'UPDATE') {
            toast({
              title: "Orçamento atualizado",
              description: "Um orçamento foi atualizado.",
            });
          } else if (payload.eventType === 'DELETE') {
            toast({
              title: "Orçamento removido",
              description: "Um orçamento foi removido.",
            });
          }
          
          // Force refetch
          refetch();
        }
      )
      .subscribe((status) => {
        console.log("Quotation channel subscription status:", status);
        if (status === 'SUBSCRIBED') {
          console.log("Successfully subscribed to quotations real-time updates");
        }
      });

    return () => {
      console.log("Cleaning up quotation real-time listener");
      supabase.removeChannel(channel);
    };
  }, [queryClient, refetch, toast]);

  // Function to manually refresh quotations and clear cache
  const refreshQuotations = async () => {
    try {
      console.log("Atualizando dados de orçamentos...");
      
      // Clear service worker cache
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_QUOTATIONS_CACHE'
        });
      }
      
      // Clear React Query cache
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      
      // Force refetch with network request
      const result = await refetch();
      
      if (result.isSuccess) {
        console.log(`Orçamentos recarregados: ${result.data?.length || 0}`);
      }
      
      toast({
        title: "Dados atualizados",
        description: result.isSuccess 
          ? `${result.data?.length || 0} orçamentos carregados com sucesso.` 
          : "A lista de orçamentos foi atualizada.",
      });
    } catch (error) {
      console.error("Erro ao atualizar orçamentos:", error);
      toast({
        title: "Erro ao atualizar",
        description: "Ocorreu um erro ao atualizar os dados. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0073B7] mx-auto mb-4"></div>
        <p className="text-gray-400">Carregando orçamentos...</p>
      </div>
    );
  }
  
  // Se chegou aqui e não tem dados nem está carregando, é um erro
  if (!quotations || quotations.length === 0) {
    return (
      <div className="w-full h-full p-6">
        <div className="flex justify-end mb-4">
          <Button 
            onClick={refreshQuotations}
            className="bg-[#0073B7] hover:bg-[#004080] text-white flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar Dados
          </Button>
        </div>
        
        <div className="text-center p-8 bg-gray-800 rounded-lg">
          <p className="text-gray-300 mb-4">
            Não foram encontrados orçamentos. Clique em "Atualizar Dados" para tentar novamente ou crie um novo orçamento.
          </p>
          <Button 
            onClick={refreshQuotations}
            className="bg-[#0073B7] hover:bg-[#004080] text-white mt-2"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar Dados
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-6">
      <div className="flex justify-end mb-4">
        <Button 
          onClick={refreshQuotations}
          className="bg-[#0073B7] hover:bg-[#004080] text-white flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Atualizar Dados
        </Button>
      </div>
      
      <QuotationsDataTable 
        quotations={quotations} 
        onEdit={onEdit}
        onDelete={onDelete}
        onView={onView}
        onShare={onShare}
      />
    </div>
  );
}
